import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Plus, Upload, Search, Edit, Trash2, Mail, Phone, Eye, Send,
  Copy, Download, Lock, Unlock, KeyRound, Ban, RotateCcw, History, CopyX
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DuplicatesDialog } from './DuplicatesDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('admin_token')}`
});

const applyAttendeeFilters = (attendees, { searchTerm, filterStatus, filterRoom, filterShirt, sortField, sortDir }) => {
  let rows = attendees;
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    rows = rows.filter(a =>
      a.first_name.toLowerCase().includes(term) ||
      a.last_name.toLowerCase().includes(term) ||
      a.email.toLowerCase().includes(term)
    );
  }
  if (filterStatus !== 'all') rows = rows.filter(a => a.ticket_status === filterStatus);
  if (filterRoom !== 'all') rows = rows.filter(a => (a.room_preference || '') === filterRoom);
  if (filterShirt !== 'all') rows = rows.filter(a => (a.tshirt_size || '').toLowerCase() === filterShirt.toLowerCase());

  // Sort — defaults to name A→Z. Empty values always land at the end regardless of direction.
  const field = sortField || 'name';
  const dir = sortDir === 'desc' ? -1 : 1;
  const numericFields = new Set(['amount_paid']);
  rows = [...rows].sort((a, b) => {
    let av, bv;
    if (field === 'name') {
      av = `${a.last_name || ''} ${a.first_name || ''}`.trim().toLowerCase();
      bv = `${b.last_name || ''} ${b.first_name || ''}`.trim().toLowerCase();
    } else if (numericFields.has(field)) {
      av = Number(a[field] || 0);
      bv = Number(b[field] || 0);
    } else {
      av = (a[field] || '').toString().toLowerCase();
      bv = (b[field] || '').toString().toLowerCase();
    }
    const aEmpty = av === '' || av === 0;
    const bEmpty = bv === '' || bv === 0;
    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
  return rows;
};

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '',
  ticket_status: 'pending', amount_paid: '', scholarship_amount: '',
  room_preference: '', tshirt_size: '',
  dietary_restrictions: '', full_commitment: '',
  needs_financial_assistance: '', willing_to_serve: '',
  volunteer_role: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  accessibility_accommodations: '',
  arrival_date: '', departure_date: '', registration_date: '',
  acceptance_status: '',
  room_id: '', bed_assignment: '',
  notes: '', tags: [], tagsInput: '',
  manual_assign_only: false
};

const STATUS_CONFIG = {
  paid_full: { label: 'Paid Full', className: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  deposit_paid: { label: 'Deposit', className: 'bg-amber-100 text-amber-800 border-amber-200' },
  pending: { label: 'Pending', className: 'bg-orange-100 text-orange-800 border-orange-200' },
  waitlist: { label: 'Waitlist', className: 'bg-violet-100 text-violet-800 border-violet-200' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800 border-red-200' }
};

const AttendeeManagement = () => {
  const [attendees, setAttendees] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRoom, setFilterRoom] = useState('all');
  const [filterShirt, setFilterShirt] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  // Set-password modal state
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [settingPassword, setSettingPassword] = useState(false);
  // Email-history modal state
  const [emailLogTarget, setEmailLogTarget] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  // Duplicates dialog
  const [showDuplicates, setShowDuplicates] = useState(false);

  const openEmailLog = async (attendee) => {
    setEmailLogTarget(attendee);
    setEmailLogLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/retreat/attendees/${attendee.id}/email-log`, { headers: getAuthHeaders() });
      setEmailLogs(res.data);
    } catch {
      toast.error('Failed to load email history');
    } finally {
      setEmailLogLoading(false);
    }
  };

  const fetchAttendees = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/retreat/attendees`, {
        headers: getAuthHeaders()
      });
      setAttendees(response.data);
    } catch {
      toast.error('Failed to fetch attendees');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAttendees(); }, [fetchAttendees]);

  // Rooms list for the assignment dropdown in the edit modal
  useEffect(() => {
    axios
      .get(`${BACKEND_URL}/api/retreat/rooms`, { headers: getAuthHeaders() })
      .then((res) => setRooms(Array.isArray(res.data) ? res.data : []))
      .catch(() => setRooms([]));
  }, []);

  // Derived filter — recomputed on every render. Cheap enough for ~150 rows.
  const filteredAttendees = applyAttendeeFilters(attendees, { searchTerm, filterStatus, filterRoom, filterShirt, sortField, sortDir });

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };
  const sortArrow = (field) => sortField !== field ? '' : (sortDir === 'asc' ? ' ↑' : ' ↓');

  const openAddModal = () => {
    setEditingAttendee(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEditModal = (attendee) => {
    setEditingAttendee(attendee);
    setFormData({
      first_name: attendee.first_name || '',
      last_name: attendee.last_name || '',
      email: attendee.email || '',
      phone: attendee.phone || '',
      ticket_status: attendee.ticket_status || 'pending',
      amount_paid: attendee.amount_paid || '',
      scholarship_amount: attendee.scholarship_amount !== null && attendee.scholarship_amount !== undefined ? attendee.scholarship_amount : '',
      room_preference: attendee.room_preference || '',
      tshirt_size: attendee.tshirt_size || '',
      thursday_dinner_rsvp: attendee.thursday_dinner_rsvp || '',
      dietary_restrictions: attendee.dietary_restrictions || '',
      full_commitment: attendee.full_commitment || '',
      needs_financial_assistance: attendee.needs_financial_assistance || '',
      willing_to_serve: attendee.willing_to_serve || '',
      volunteer_role: attendee.volunteer_role || '',
      emergency_contact_name: attendee.emergency_contact_name || '',
      emergency_contact_phone: attendee.emergency_contact_phone || '',
      accessibility_accommodations: attendee.accessibility_accommodations || '',
      arrival_date: attendee.arrival_date || '',
      departure_date: attendee.departure_date || '',
      registration_date: attendee.registration_date || '',
      acceptance_status: attendee.acceptance_status || '',
      room_id: attendee.room_id || '',
      bed_assignment: attendee.bed_assignment || '',
      notes: attendee.notes || '',
      tags: attendee.tags || [],
      tagsInput: (attendee.tags || []).join(', '),
      manual_assign_only: !!attendee.manual_assign_only
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.first_name || !formData.last_name || !formData.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    setSaving(true);
    const payload = { ...formData };
    // tagsInput → tags[]
    if (payload.tagsInput !== undefined) {
      payload.tags = payload.tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      delete payload.tagsInput;
    }
    // Amount fields: empty → null
    if (payload.amount_paid === '') delete payload.amount_paid;
    else payload.amount_paid = parseFloat(payload.amount_paid) || null;
    // Blank room_id means "no assignment" — send empty string, backend accepts null-ish
    if (payload.room_id === '') payload.room_id = null;
    // scholarship_amount: empty string = not on scholarship (null), 0 = scholarship granted (amount TBD), >0 = specific amount
    if (payload.scholarship_amount === '' || payload.scholarship_amount === null) {
      payload.scholarship_amount = null;
    } else {
      const parsed = parseFloat(payload.scholarship_amount);
      payload.scholarship_amount = isNaN(parsed) ? null : parsed;
    }
    try {
      if (editingAttendee) {
        await axios.put(`${BACKEND_URL}/api/retreat/attendees/${editingAttendee.id}`, payload, { headers: getAuthHeaders() });
        toast.success('Attendee updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/retreat/attendees`, payload, { headers: getAuthHeaders() });
        toast.success('Attendee added');
      }
      setShowModal(false);
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save attendee');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (attendee) => {
    if (!window.confirm(`Delete ${attendee.first_name} ${attendee.last_name}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/retreat/attendees/${attendee.id}`, { headers: getAuthHeaders() });
      toast.success('Attendee deleted');
      fetchAttendees();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/retreat/attendees/import`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      const d = response.data || {};
      const parts = [
        d.imported > 0 ? `${d.imported} new attendees` : null,
        d.updated_payment > 0 ? `${d.updated_payment} payment updates` : null,
        d.logins_created > 0 ? `${d.logins_created} logins created` : null,
      ].filter(Boolean);
      const message = parts.length
        ? `Imported: ${parts.join(' · ')}`
        : `No changes — all ${d.skipped || 0} rows already up to date`;
      toast.success(message, { duration: 6000 });
      if (d.downgrades_skipped && d.downgrades_skipped.length > 0) {
        toast.info(
          `${d.downgrades_skipped.length} app-side change(s) protected (sheet had older/blank data) — see console for details`,
          { duration: 8000 }
        );
        console.warn('[CSV import — protected app changes]', d.downgrades_skipped);
      }
      if (d.errors && d.errors.length > 0) {
        toast.error(`${d.errors.length} row(s) had errors — see console`, { duration: 6000 });
        console.warn('[CSV import errors]', d.errors);
      }
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    }
    event.target.value = '';
  };

  const handleCopyPortalLink = (attendee) => {
    const link = `${window.location.origin}/portal/${attendee.id}`;
    navigator.clipboard.writeText(link);
    toast.success(`Portal link copied for ${attendee.first_name}`);
  };

  const handleSendInvite = async (attendee) => {
    setSendingInvite(attendee.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/retreat/invites/send`,
        { email: attendee.email, role: 'attendee', attendee_id: attendee.id },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.email_sent ? `Invite sent to ${attendee.email}` : 'Invite created');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setSendingInvite(null);
    }
  };

  const openSetPassword = (attendee) => {
    setPasswordTarget(attendee);
    setNewPassword('');
  };

  const handleSetPassword = async () => {
    if (!passwordTarget) return;
    if (newPassword.trim().length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSettingPassword(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/attendees/${passwordTarget.id}/set-password`,
        { password: newPassword },
        { headers: getAuthHeaders() }
      );
      const verb = res.data.created_new_account ? 'Account created for' : 'Password updated for';
      toast.success(`${verb} ${passwordTarget.first_name} (${res.data.email})`);
      setPasswordTarget(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setSettingPassword(false);
    }
  };

  const handleToggleManual = async (attendee) => {
    const newValue = !attendee.manual_assign_only;
    try {
      await axios.put(
        `${BACKEND_URL}/api/retreat/attendees/${attendee.id}`,
        { manual_assign_only: newValue },
        { headers: getAuthHeaders() }
      );
      toast.success(
        newValue
          ? `${attendee.first_name} marked as manual-only (excluded from auto-assign)`
          : `${attendee.first_name} re-enabled for auto-assign`
      );
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleQuickCancel = async (attendee) => {
    const ok = window.confirm(
      `Mark ${attendee.first_name} ${attendee.last_name} as cancelled?\n\n` +
      `They'll be moved to the "Cancelled" filter. You can restore them anytime from this table.`
    );
    if (!ok) return;
    try {
      await axios.post(
        `${BACKEND_URL}/api/retreat/attendees/${attendee.id}/cancel`,
        { reason: null, notify_admin_email: false },
        { headers: getAuthHeaders() }
      );
      toast.success(`${attendee.first_name} marked as cancelled`);
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel attendance');
    }
  };

  const handleRestoreAttendance = async (attendee) => {
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/retreat/attendees/${attendee.id}/restore`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`${attendee.first_name} restored to ${res.data.ticket_status.replace('_', ' ')}`);
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore attendance');
    }
  };

  const handleExportCSV = () => {
    const headers = ['First Name','Last Name','Email','Phone','Status','Amount Paid','Room Preference','T-Shirt Size','Dietary Restrictions','Financial Assistance','Volunteer','Notes'];
    const rows = filteredAttendees.map(a => [
      a.first_name, a.last_name, a.email, a.phone || '', a.ticket_status,
      a.amount_paid || '', a.room_preference || '', a.tshirt_size || '',
      a.dietary_restrictions || '', a.needs_financial_assistance || '',
      a.willing_to_serve || '', a.notes || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'attendees_export.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const handleExportAll = () => {
    // Combined attendees + waitlist, 100% alphabetized by last name then first name.
    // Both live in the same `attendees` state (waitlist = ticket_status='waitlist').
    const sorted = [...attendees].sort((a, b) => {
      const la = (a.last_name || '').toLowerCase();
      const lb = (b.last_name || '').toLowerCase();
      if (la !== lb) return la.localeCompare(lb);
      const fa = (a.first_name || '').toLowerCase();
      const fb = (b.first_name || '').toLowerCase();
      return fa.localeCompare(fb);
    });
    const headers = ['First Name','Last Name','Email','Phone','Payment Status','Amount Paid'];
    const statusLabel = {
      paid_full: 'Paid in full',
      deposit_paid: 'Deposit paid',
      pending: 'Pending',
      waitlist: 'Waitlist',
      cancelled: 'Cancelled',
    };
    const rows = sorted.map(a => [
      a.first_name || '',
      a.last_name || '',
      a.email || '',
      a.phone || '',
      statusLabel[a.ticket_status] || a.ticket_status || '',
      Number(a.amount_paid || 0).toFixed(2),
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().split('T')[0];
    a.download = `rojo_all_attendees_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported all ${sorted.length} records (alphabetized)`);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredAttendees.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredAttendees.map(a => a.id));
    }
  };

  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.length === 0) return;
    try {
      await Promise.all(selectedIds.map(id =>
        axios.put(`${BACKEND_URL}/api/retreat/attendees/${id}`, { ticket_status: newStatus }, { headers: getAuthHeaders() })
      ));
      toast.success(`Updated ${selectedIds.length} attendees to ${newStatus}`);
      setSelectedIds([]);
      fetchAttendees();
    } catch {
      toast.error('Some updates failed');
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="attendee-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="attendee-management">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Attendees</h1>
          <p className="text-gray-600 mt-1">{filteredAttendees.length} of {attendees.length} attendees</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowDuplicates(true)} data-testid="check-duplicates-btn">
            <CopyX className="w-4 h-4 mr-2" />Duplicates
          </Button>
          <Button
            onClick={handleExportAll}
            className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white"
            data-testid="export-all-btn"
          >
            <Download className="w-4 h-4 mr-2" />Export All
          </Button>
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />Export View
          </Button>
          <label htmlFor="csv-upload">
            <input id="csv-upload" type="file" accept=".csv" onChange={handleImportCSV} className="hidden" />
            <Button variant="outline" className="cursor-pointer" asChild>
              <span><Upload className="w-4 h-4 mr-2" />Import CSV</span>
            </Button>
          </label>
          <Button onClick={openAddModal} className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600" data-testid="add-attendee-btn">
            <Plus className="w-4 h-4 mr-2" />Add Attendee
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input placeholder="Search by name or email..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="attendee-search" />
            </div>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border rounded-md px-3 py-2 text-sm" data-testid="attendee-status-filter">
              <option value="all">All Status</option>
              <option value="paid_full">Paid Full</option>
              <option value="deposit_paid">Deposit</option>
              <option value="pending">Pending</option>
              <option value="waitlist">Waitlist</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <select value={filterRoom} onChange={(e) => setFilterRoom(e.target.value)} className="border rounded-md px-3 py-2 text-sm" data-testid="filter-room-pref">
              <option value="all">All Room Prefs</option>
              <option value="shared">Shared</option>
              <option value="double">Double</option>
              <option value="single">Single</option>
            </select>
            <select value={filterShirt} onChange={(e) => setFilterShirt(e.target.value)} className="border rounded-md px-3 py-2 text-sm" data-testid="filter-shirt-size">
              <option value="all">All Shirts</option>
              <option value="S">S</option>
              <option value="M">M</option>
              <option value="L">L</option>
              <option value="XL">XL</option>
              <option value="2XL">2XL</option>
              <option value="3XL">3XL</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk actions */}
      {selectedIds.length > 0 && (
        <Card className="mb-4 border-orange-200 bg-orange-50">
          <CardContent className="py-3 flex items-center justify-between">
            <p className="text-sm font-medium text-orange-800">{selectedIds.length} selected</p>
            <div className="flex gap-2">
              <select onChange={(e) => { if (e.target.value) { handleBulkStatusChange(e.target.value); e.target.value = ''; } }} className="border rounded-md px-2 py-1 text-xs">
                <option value="">Change status...</option>
                <option value="paid_full">Paid Full</option>
                <option value="deposit_paid">Deposit</option>
                <option value="pending">Pending</option>
                <option value="waitlist">Waitlist</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds([])}>Clear</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="pt-4">
          {filteredAttendees.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="no-attendees">
              <p className="text-lg">No attendees found</p>
              <p className="text-sm mt-1">Add your first attendee or import from CSV</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="attendees-table">
                <thead>
                  <tr className="border-b text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-3 px-3 text-left">
                      <input type="checkbox" checked={selectedIds.length === filteredAttendees.length && filteredAttendees.length > 0} onChange={toggleSelectAll} className="rounded border-gray-300" />
                    </th>
                    <th className="py-3 px-3 text-left">
                      <button type="button" onClick={() => toggleSort('name')} className="uppercase tracking-wide font-semibold hover:text-wine transition-colors" data-testid="sort-name">
                        Name{sortArrow('name')}
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left hidden md:table-cell">
                      <button type="button" onClick={() => toggleSort('email')} className="uppercase tracking-wide font-semibold hover:text-wine transition-colors" data-testid="sort-email">
                        Contact{sortArrow('email')}
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left">
                      <button type="button" onClick={() => toggleSort('ticket_status')} className="uppercase tracking-wide font-semibold hover:text-wine transition-colors" data-testid="sort-status">
                        Status{sortArrow('ticket_status')}
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left hidden lg:table-cell">
                      <button type="button" onClick={() => toggleSort('amount_paid')} className="uppercase tracking-wide font-semibold hover:text-wine transition-colors" data-testid="sort-paid">
                        Paid{sortArrow('amount_paid')}
                      </button>
                    </th>
                    <th className="py-3 px-3 text-left hidden lg:table-cell">Room Pref</th>
                    <th className="py-3 px-3 text-left hidden xl:table-cell">Shirt</th>
                    <th className="py-3 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendees.map((attendee) => (
                    <tr key={attendee.id} className="border-b hover:bg-gray-50 transition-colors" data-testid={`attendee-row-${attendee.id}`}>
                      <td className="py-3 px-3">
                        <input type="checkbox" checked={selectedIds.includes(attendee.id)} onChange={() => setSelectedIds(prev => prev.includes(attendee.id) ? prev.filter(id => id !== attendee.id) : [...prev, attendee.id])} className="rounded border-gray-300" />
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-wine to-burgundy rounded-full flex items-center justify-center text-white font-semibold text-xs shrink-0">
                            {attendee.first_name[0]}{attendee.last_name[0]}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-gray-900 text-sm truncate">{attendee.first_name} {attendee.last_name}</p>
                              {attendee.manual_assign_only && (
                                <span
                                  className="text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0"
                                  title="Excluded from auto-assign"
                                  data-testid={`manual-badge-${attendee.id}`}
                                >
                                  Manual
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 md:hidden truncate">{attendee.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 hidden md:table-cell">
                        <div className="text-xs space-y-0.5">
                          <div className="flex items-center gap-1.5 text-gray-600"><Mail className="w-3 h-3 shrink-0" /><span className="truncate max-w-[160px]">{attendee.email}</span></div>
                          {attendee.phone && <div className="flex items-center gap-1.5 text-gray-600"><Phone className="w-3 h-3 shrink-0" />{attendee.phone}</div>}
                        </div>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {getStatusBadge(attendee.ticket_status)}
                          {attendee.scholarship_amount !== null && attendee.scholarship_amount !== undefined && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-pink-100 text-pink-700 border border-pink-200" title={`Scholarship covers $${attendee.scholarship_amount}`}>
                              Scholarship
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-3 hidden lg:table-cell">
                        <span className="text-sm text-gray-700">{attendee.amount_paid ? `$${attendee.amount_paid}` : '—'}</span>
                      </td>
                      <td className="py-3 px-3 hidden lg:table-cell">
                        <span className="text-sm text-gray-600 capitalize">{attendee.room_preference || '—'}</span>
                      </td>
                      <td className="py-3 px-3 hidden xl:table-cell">
                        <span className="text-sm text-gray-600">{attendee.tshirt_size || '—'}</span>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex justify-end gap-0.5">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleToggleManual(attendee)}
                            className={`h-8 w-8 p-0 ${attendee.manual_assign_only ? 'text-amber-600 hover:text-amber-700' : 'text-gray-400 hover:text-amber-600'}`}
                            title={attendee.manual_assign_only ? 'Manual-only — click to allow auto-assign' : 'Auto-eligible — click to mark manual-only'}
                            data-testid={`toggle-manual-${attendee.id}`}
                          >
                            {attendee.manual_assign_only ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCopyPortalLink(attendee)} className="text-gray-400 hover:text-gray-700 h-8 w-8 p-0" title="Copy portal link" data-testid={`copy-link-${attendee.id}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleSendInvite(attendee)} disabled={sendingInvite === attendee.id} className="text-gray-400 hover:text-orange-600 h-8 w-8 p-0" title="Send invite" data-testid={`send-invite-${attendee.id}`}>
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => window.open(`/portal/${attendee.id}`, '_blank')} className="text-gray-400 hover:text-blue-600 h-8 w-8 p-0" title="Preview portal" data-testid={`preview-portal-${attendee.id}`}>
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openSetPassword(attendee)} className="text-gray-400 hover:text-purple-600 h-8 w-8 p-0" title="Set password" data-testid={`set-password-${attendee.id}`}>
                            <KeyRound className="w-3.5 h-3.5" />
                          </Button>
                          {attendee.ticket_status === 'cancelled' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRestoreAttendance(attendee)}
                              className="text-emerald-500 hover:text-emerald-700 h-8 w-8 p-0"
                              title="Restore — bring them back from cancelled"
                              data-testid={`restore-attendee-${attendee.id}`}
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleQuickCancel(attendee)}
                              className="text-gray-400 hover:text-red-600 h-8 w-8 p-0"
                              title="Mark as cancelled"
                              data-testid={`cancel-attendee-${attendee.id}`}
                            >
                              <Ban className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => openEmailLog(attendee)} className="text-gray-400 hover:text-blue-600 h-8 w-8 p-0" title="Email history" data-testid={`email-log-${attendee.id}`}>
                            <History className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(attendee)} className="text-gray-400 hover:text-gray-700 h-8 w-8 p-0" data-testid={`edit-attendee-${attendee.id}`}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(attendee)} className="text-gray-400 hover:text-red-600 h-8 w-8 p-0" data-testid={`delete-attendee-${attendee.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="attendee-modal">
          <DialogHeader>
            <DialogTitle>{editingAttendee ? 'Edit Attendee' : 'Add Attendee'}</DialogTitle>
            <DialogDescription>
              {editingAttendee
                ? `${editingAttendee.first_name} ${editingAttendee.last_name} · ${editingAttendee.email}`
                : 'Add a new attendee to the retreat'}
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="contact" className="w-full mt-2">
            <TabsList className="w-full flex flex-wrap justify-start gap-1 h-auto bg-cream-100 p-1">
              <TabsTrigger value="contact" data-testid="tab-contact">Contact</TabsTrigger>
              <TabsTrigger value="payment" data-testid="tab-payment">Payment</TabsTrigger>
              <TabsTrigger value="preferences" data-testid="tab-preferences">Preferences</TabsTrigger>
              <TabsTrigger value="logistics" data-testid="tab-logistics">Logistics</TabsTrigger>
              <TabsTrigger value="emergency" data-testid="tab-emergency">Emergency</TabsTrigger>
              <TabsTrigger value="advanced" data-testid="tab-advanced">Advanced</TabsTrigger>
            </TabsList>

            {/* Contact */}
            <TabsContent value="contact" className="space-y-4 pt-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">First Name *</Label>
                  <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} data-testid="attendee-first-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Last Name *</Label>
                  <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} data-testid="attendee-last-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="attendee-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Phone</Label>
                  <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} data-testid="attendee-phone" />
                </div>
              </div>
            </TabsContent>

            {/* Payment */}
            <TabsContent value="payment" className="space-y-4 pt-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Payment Status</Label>
                  <Select value={formData.ticket_status} onValueChange={(v) => setFormData({ ...formData, ticket_status: v })}>
                    <SelectTrigger data-testid="attendee-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="deposit_paid">Deposit Paid</SelectItem>
                      <SelectItem value="paid_full">Paid in Full</SelectItem>
                      <SelectItem value="waitlist">Waitlist</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Amount Paid ($)</Label>
                  <Input type="number" value={formData.amount_paid} onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })} placeholder="e.g. 325" data-testid="form-amount-paid" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span>Scholarship Amount ($)</span>
                    <span className="text-[10px] font-normal text-gray-400">leave blank if none</span>
                  </Label>
                  <Input type="number" value={formData.scholarship_amount} onChange={(e) => setFormData({ ...formData, scholarship_amount: e.target.value })} placeholder="e.g. 200 (use 0 if amount TBD)" data-testid="form-scholarship-amount" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Needs Financial Assistance?</Label>
                  <Input value={formData.needs_financial_assistance} onChange={(e) => setFormData({ ...formData, needs_financial_assistance: e.target.value })} placeholder="Yes / No / details" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Acceptance Status</Label>
                  <Input value={formData.acceptance_status} onChange={(e) => setFormData({ ...formData, acceptance_status: e.target.value })} placeholder="accepted / pending / declined" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Registration Date</Label>
                  <Input type="date" value={formData.registration_date} onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })} />
                </div>
              </div>
            </TabsContent>

            {/* Preferences */}
            <TabsContent value="preferences" className="space-y-4 pt-5">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Room Preference</Label>
                  <Select value={formData.room_preference || 'none'} onValueChange={(v) => setFormData({ ...formData, room_preference: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No preference</SelectItem>
                      <SelectItem value="shared">Shared</SelectItem>
                      <SelectItem value="double">Double</SelectItem>
                      <SelectItem value="single">Single</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">T-Shirt Size</Label>
                  <Select value={formData.tshirt_size || 'none'} onValueChange={(v) => setFormData({ ...formData, tshirt_size: v === 'none' ? '' : v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">N/A</SelectItem>
                      <SelectItem value="S">S</SelectItem>
                      <SelectItem value="M">M</SelectItem>
                      <SelectItem value="L">L</SelectItem>
                      <SelectItem value="XL">XL</SelectItem>
                      <SelectItem value="2XL">2XL</SelectItem>
                      <SelectItem value="3XL">3XL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Willing to Volunteer?</Label>
                  <Input value={formData.willing_to_serve} onChange={(e) => setFormData({ ...formData, willing_to_serve: e.target.value })} placeholder="Yes / No" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Volunteer Role</Label>
                  <Input value={formData.volunteer_role} onChange={(e) => setFormData({ ...formData, volunteer_role: e.target.value })} placeholder="e.g. Kitchen · Setup · Sound" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Full Commitment?</Label>
                  <Input value={formData.full_commitment} onChange={(e) => setFormData({ ...formData, full_commitment: e.target.value })} placeholder="Can attend full retreat?" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Dietary Restrictions</Label>
                <Input value={formData.dietary_restrictions} onChange={(e) => setFormData({ ...formData, dietary_restrictions: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Accessibility Accommodations (CPAP, mobility, etc.)</Label>
                <Input value={formData.accessibility_accommodations} onChange={(e) => setFormData({ ...formData, accessibility_accommodations: e.target.value })} data-testid="attendee-accessibility" />
              </div>
            </TabsContent>

            {/* Logistics — room assignment + travel dates */}
            <TabsContent value="logistics" className="space-y-4 pt-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Assigned Room</Label>
                  <Select value={formData.room_id || 'none'} onValueChange={(v) => setFormData({ ...formData, room_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="attendee-room-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="none">— Unassigned —</SelectItem>
                      {rooms.map((r) => (
                        <SelectItem key={r.id} value={r.id}>
                          {r.name || r.title || 'Room'} {r.capacity ? `(cap ${r.capacity})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Bed Assignment</Label>
                  <Input value={formData.bed_assignment} onChange={(e) => setFormData({ ...formData, bed_assignment: e.target.value })} placeholder="e.g. Top bunk / Bed 3" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Arrival Date</Label>
                  <Input type="date" value={formData.arrival_date} onChange={(e) => setFormData({ ...formData, arrival_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Departure Date</Label>
                  <Input type="date" value={formData.departure_date} onChange={(e) => setFormData({ ...formData, departure_date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Thursday Cookout RSVP</Label>
                  <Select value={formData.thursday_dinner_rsvp || 'none'} onValueChange={(v) => setFormData({ ...formData, thursday_dinner_rsvp: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="attendee-dinner-rsvp-select"><SelectValue placeholder="No reply" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No reply —</SelectItem>
                      <SelectItem value="yes">Yes — attending</SelectItem>
                      <SelectItem value="no">No — can&apos;t make it</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50/50 cursor-pointer hover:bg-amber-50">
                <input
                  type="checkbox"
                  checked={!!formData.manual_assign_only}
                  onChange={(e) => setFormData({ ...formData, manual_assign_only: e.target.checked })}
                  className="mt-0.5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  data-testid="attendee-manual-only"
                />
                <div className="text-xs">
                  <p className="font-semibold text-amber-900">Manual room assignment only</p>
                  <p className="text-amber-700 mt-0.5">Exclude from auto-generated room groupings. Useful for staff, coordinators, or anyone with custom rooming.</p>
                </div>
              </label>
            </TabsContent>

            {/* Emergency */}
            <TabsContent value="emergency" className="space-y-4 pt-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Emergency Contact Name</Label>
                  <Input value={formData.emergency_contact_name} onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Emergency Contact Phone</Label>
                  <Input value={formData.emergency_contact_phone} onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })} />
                </div>
              </div>
            </TabsContent>

            {/* Advanced — tags + notes + read-only lifecycle */}
            <TabsContent value="advanced" className="space-y-4 pt-5">
              <div className="space-y-1.5">
                <Label className="text-xs">Tags <span className="text-[10px] font-normal text-gray-400">(comma-separated, e.g. vip, staff, sponsor)</span></Label>
                <Input
                  value={formData.tagsInput ?? ''}
                  onChange={(e) => setFormData({ ...formData, tagsInput: e.target.value })}
                  placeholder="vip, staff, sponsor"
                  data-testid="attendee-tags"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Notes / Comments</Label>
                <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} />
              </div>
              {editingAttendee && (
                <div className="grid grid-cols-2 gap-3 pt-3 border-t border-cream-200 text-xs text-gray-500">
                  <div><span className="font-semibold text-gray-700">Attendee ID:</span> <span className="font-mono">{editingAttendee.id.slice(0, 8)}…</span></div>
                  <div><span className="font-semibold text-gray-700">Created:</span> {editingAttendee.created_at ? new Date(editingAttendee.created_at).toLocaleDateString() : '—'}</div>
                  <div><span className="font-semibold text-gray-700">Last updated:</span> {editingAttendee.updated_at ? new Date(editingAttendee.updated_at).toLocaleString() : '—'}</div>
                  <div><span className="font-semibold text-gray-700">Checked-in:</span> {editingAttendee.checked_in_at ? new Date(editingAttendee.checked_in_at).toLocaleString() : '—'}</div>
                  {editingAttendee.cancelled_at && (
                    <div className="col-span-2"><span className="font-semibold text-gray-700">Cancelled:</span> {new Date(editingAttendee.cancelled_at).toLocaleString()} {editingAttendee.cancelled_by ? `by ${editingAttendee.cancelled_by}` : ''}</div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-wine to-burgundy hover:from-wine-600 hover:to-burgundy-600 text-white" data-testid="save-attendee-btn">
              {saving ? 'Saving…' : editingAttendee ? 'Update' : 'Add Attendee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Set Password Dialog (admin-only manual override) */}
      <Dialog
        open={!!passwordTarget}
        onOpenChange={(open) => {
          if (!open) {
            setPasswordTarget(null);
            setNewPassword('');
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="set-password-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-purple-600" />
              Set Password
            </DialogTitle>
            <DialogDescription>
              {passwordTarget && (
                <>
                  Manually set a sign-in password for{' '}
                  <span className="font-semibold text-gray-900">
                    {passwordTarget.first_name} {passwordTarget.last_name}
                  </span>{' '}
                  <span className="text-gray-500">({passwordTarget.email})</span>.
                  Share the new password with them directly — they&apos;ll use it to sign in at{' '}
                  <span className="font-medium">/login</span>.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-xs text-gray-600">New password (min 6 characters)</Label>
            <Input
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="e.g. NewPass2026"
              className="font-mono"
              autoFocus
              data-testid="set-password-input"
            />
            <p className="text-[11px] text-gray-500 leading-relaxed">
              Tip: keep it simple — they&apos;ll likely type it from a text/email. Avoid characters
              that get auto-corrected by phones (em dashes, smart quotes).
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setPasswordTarget(null);
                setNewPassword('');
              }}
              data-testid="set-password-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSetPassword}
              disabled={settingPassword || newPassword.trim().length < 6}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="set-password-save"
            >
              {settingPassword ? 'Saving...' : 'Save Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicates dialog */}
      <DuplicatesDialog open={showDuplicates} onOpenChange={setShowDuplicates} onMerged={fetchAttendees} />

      {/* Email history modal */}
      <Dialog open={!!emailLogTarget} onOpenChange={(open) => { if (!open) { setEmailLogTarget(null); setEmailLogs([]); } }}>
        <DialogContent data-testid="email-log-dialog">
          <DialogHeader>
            <DialogTitle>
              Email history — {emailLogTarget?.first_name} {emailLogTarget?.last_name}
            </DialogTitle>
          </DialogHeader>
          {emailLogLoading ? (
            <p className="py-8 text-center text-sm text-gray-400">Loading…</p>
          ) : emailLogs.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400" data-testid="email-log-empty">
              No emails on record for this attendee yet
            </p>
          ) : (
            <div className="max-h-80 overflow-y-auto space-y-2" data-testid="email-log-list">
              {emailLogs.map((log) => (
                <div key={log.id} className="p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 truncate">{log.subject}</p>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${log.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {log.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {log.category} · {new Date(log.sent_at).toLocaleString()} · by {log.sent_by}
                  </p>
                  {log.error && <p className="text-[11px] text-red-500 mt-1">{log.error}</p>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AttendeeManagement;
